import { useLanguage } from '../contexts/LanguageContext';
import {
  breadcrumbList,
  canonicalUrl as buildCanonical,
  faqPage,
  hreflangAlternates,
  metaDescription,
  pageTitle,
  productJsonLd,
  serializeJsonLd,
  SITE_URL,
} from '../utils/seo';
import { useHead } from '../utils/seo/head';

// The one head component for every page (utils/seo/head.js useHead; the
// page language and direction on <html> come from LanguageContext). Titles, descriptions,
// canonicals and JSON-LD come from the builders in utils/seo/ so the React
// pages and the backend crawler pages (backend/utils/seo.py) say the same.
export { productJsonLd };

const OG_IMAGE  = 'https://anum360.ae/og-image.jpg';
const SITE_NAME = 'ANUM 360 GALLERY';

const DEFAULT_TITLE = { en: 'Authentic Luxury Fashion, Abu Dhabi, UAE', ar: 'أزياء فاخرة أصلية، أبوظبي، الإمارات' };
const DEFAULT_DESCRIPTION = {
  en: 'Discover authenticated luxury fashion, designer bags, fine jewellery, perfumes and accessories. Abu Dhabi\'s luxury boutique, delivering across the UAE.',
  ar: 'اكتشف أزياء فاخرة أصلية وحقائب مصممين ومجوهرات وعطورًا وإكسسوارات. متجر فاخر في أبوظبي مع التوصيل في جميع أنحاء الإمارات.',
};

const locationParts = () => {
  if (typeof window === 'undefined') return { pathname: '/', search: '' };
  return { pathname: window.location.pathname, search: window.location.search };
};

const SEOHead = ({
  title,
  titleAr,       // Arabic <title> (used when the page is shown in Arabic)
  description,
  descriptionAr,
  keywords    = 'luxury fashion UAE, authentic designer bags Abu Dhabi, fine jewellery UAE, luxury boutique Abu Dhabi, ANUM 360',
  image       = OG_IMAGE,
  type        = 'website',
  url,           // site-relative path (+ query) of the canonical page; default: the current URL
  article,       // { publishedTime, modifiedTime, author } for blog posts
  product,       // product data for product pages
  faqs,          // [{ question, answer }] for FAQ rich results
  breadcrumbs,   // [{ name, url }] (url relative to the site) for BreadcrumbList
  jsonLd,        // extra JSON-LD objects (ItemList, reviews, …); falsy entries skipped
  noIndex       = false,
}) => {
  const { language } = useLanguage();
  const ar = language === 'ar';
  const where = url
    ? { pathname: url.split('?')[0], search: url.includes('?') ? url.slice(url.indexOf('?')) : '' }
    : locationParts();
  const canonicalUrl = buildCanonical(where.pathname, where.search, { lang: ar ? 'ar' : 'en' });
  const langCode     = ar ? 'ar-AE' : 'en-AE';
  const altLang      = ar ? 'en_AE' : 'ar_AE';
  const fullTitle    = pageTitle((ar && titleAr) || title || (ar ? DEFAULT_TITLE.ar : DEFAULT_TITLE.en));
  const metaDesc     = metaDescription((ar && descriptionAr) || description || (ar ? DEFAULT_DESCRIPTION.ar : DEFAULT_DESCRIPTION.en));
  const faqLd        = faqs && faqs.length ? faqPage(faqs) : null;
  const extraLd      = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);

  const meta = [
    // ── Primary ──
    { name: 'description', content: metaDesc },
    { name: 'keywords', content: keywords },
    { name: 'robots', content: noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' },
    // ── Open Graph ──
    { property: 'og:type', content: type },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:title', content: fullTitle },
    { property: 'og:description', content: metaDesc },
    { property: 'og:image', content: image },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:locale', content: langCode.replace('-', '_') },
    { property: 'og:locale:alternate', content: altLang },
    // ── Article meta (blog) ──
    ...(article ? [
      { property: 'article:published_time', content: article.publishedTime },
      { property: 'article:modified_time', content: article.modifiedTime || article.publishedTime },
      { property: 'article:author', content: article.author || SITE_NAME },
    ] : []),
    // ── Twitter Card ──
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@anum360gallery' },
    { name: 'twitter:title', content: fullTitle },
    { name: 'twitter:description', content: metaDesc },
    { name: 'twitter:image', content: image },
    // ── Geo / Local ──
    { name: 'geo.region', content: 'AE-AZ' },
    { name: 'geo.placename', content: 'Abu Dhabi, UAE' },
    { name: 'geo.position', content: '24.4539;54.3773' },
    { name: 'ICBM', content: '24.4539, 54.3773' },
  ];

  const links = [
    { rel: 'canonical', href: canonicalUrl },
    // hreflang: English is the plain URL, Arabic is ?lang=ar (read by the
    // LanguageProvider on load), x-default = English. Private and noindex
    // pages get none.
    ...(noIndex ? [] : hreflangAlternates(canonicalUrl).map((alt) => ({ rel: 'alternate', hrefLang: alt.hrefLang, href: alt.href }))),
  ];

  const scripts = [
    // FAQ rich results
    faqLd && serializeJsonLd(faqLd),
    // Article rich results (blog)
    article && serializeJsonLd({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: fullTitle,
      description: metaDesc,
      image,
      datePublished: article.publishedTime,
      dateModified: article.modifiedTime || article.publishedTime,
      author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
      publisher: { '@type': 'Organization', name: SITE_NAME, logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    }),
    // Product rich results
    product && serializeJsonLd(productJsonLd(product, { canonicalUrl: buildCanonical(where.pathname, where.search), image })),
    // BreadcrumbList
    breadcrumbs && breadcrumbs.length > 0 && serializeJsonLd(breadcrumbList(breadcrumbs)),
    ...extraLd.map((data) => serializeJsonLd(data)),
  ].filter(Boolean);

  useHead({ title: fullTitle, meta, links, scripts });
  return null;
};

export default SEOHead;
